import React, { Component } from 'react';
import { computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { FormModal, IFieldSetPartial } from '../../../lib/mighty-fields';
import { ILoanAccount, ILoanComplianceCharge } from '../../../interfaces';
import { FormattingUtils } from '../../../utils';
import { getCurrentDate } from '../../../utils/util';

import { isChargeOpen } from './Charges';

const { LOAN_PAYMENT_TYPE_COMPLIANCE } = AppConstants
  , { formatDate, formatMoney } = FormattingUtils
  ;

interface IChargeOption {
  id: string;
  name: string;
}

// objectSelect renders each option by its `name`, and a charge has none of its own -- so one is
// built from the two things that tell them apart: when it fell due and what is left on it.
function chargeOptions (charges: ILoanComplianceCharge[]): IChargeOption[] {
  return charges
    .filter(isChargeOpen)
    .map(charge => ({
      id: charge.id,
      name: `Due ${formatDate(charge.due_date)} - ${formatMoney(charge.amount_outstanding)} outstanding`,
    }));
}

function fieldSets (charges: ILoanComplianceCharge[]): IFieldSetPartial[] {
  return [
    [
      {
        field: 'compliance_charge',
        label: 'Charge',
        objectKey: 'id',
        objects: chargeOptions(charges),
        required: true,
        type: 'objectSelect',
      },
      { field: 'amount', required: true, type: 'money' },
      { field: 'payment_date', required: true, type: 'date' },
      { field: 'notes', type: 'textDynamic' },
    ],
  ];
}

interface IProps {
  charges: ILoanComplianceCharge[];
  loanAccount: ILoanAccount;
  onClose: () => void;
  onCreate: () => Promise<void>;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
}

@inject('Clients')
@autoBindMethods
@observer
class LogCompliancePaymentModal extends Component<IProps> {
  private get injected () {
    return this.props as IInjected;
  }

  // Rebuilt only when the charges change. The parent hands the same array down until they do, and
  // mobx-react only invalidates props it finds not shallow-equal, so the option list is not
  // reassembled underneath a form the user is part way through filling in.
  @computed
  private get fieldSets () {
    return fieldSets(this.props.charges);
  }

  // A payment over what the charge has left is a 400; FormManager maps that back onto the field
  // rather than this form second-guessing the API.
  private async onSave (data: object) {
    const { Clients } = this.injected
      , { loanAccount, onCreate } = this.props
      ;

    await Clients.loanPayments.create({
      ...data,
      account: loanAccount.id,
      type: LOAN_PAYMENT_TYPE_COMPLIANCE,
    });

    await onCreate();
  }

  public render () {
    return (
      <FormModal
        className='modal-log-compliance-payment'
        fieldSets={this.fieldSets}
        // Payments are usually logged the day they arrive, so today is the answer more often than not
        model={{ payment_date: getCurrentDate() }}
        onClose={this.props.onClose}
        onSave={this.onSave}
        saveText='Log Payment'
        title='Log Compliance Payment'
      />
    );
  }
}

export default LogCompliancePaymentModal;
